<template>
  <div class="main-wrapper" v-if="items">
    <el-tabs v-model="status" @tab-click="btnNavClick">
      <el-tab-pane :label="'待开发票(' + defaultCount + ')'" :name="statusDefault"></el-tab-pane>
      <el-tab-pane :label="'已开发票(' + deliveredCount + ')'" :name="statusDelivered"></el-tab-pane>
      <el-tab-pane :label="'作废发票(' + cancellationCount + ')'" :name="statusCancellation"></el-tab-pane>
    </el-tabs>

    <el-table :data="items" style="width: 100%">
      <el-table-column label="申请时间" width="160" align="center">
        <template #default="scope">
          <el-link type="primary" :underline="false" @click.prevent="btnInvoiceClick(scope.row)">
            {{ scope.row.createdDate }}
          </el-link>
        </template>
      </el-table-column>
      <el-table-column label="用户" width="120">
        <template #default="scope">
          {{ scope.row.userName }}
        </template>
      </el-table-column>
      <el-table-column label="发票类型" width="180" align="center">
        <template #default="scope">
          <span v-if="!scope.row.isVatSpecial && scope.row.isElectronic">增值税普通发票（数电）</span>
          <span v-else-if="scope.row.isVatSpecial && scope.row.isElectronic">增值税专用发票（数电）</span>
          <span v-else-if="!scope.row.isVatSpecial && !scope.row.isElectronic">增值税普通发票（纸质）</span>
          <span v-else-if="scope.row.isVatSpecial && !scope.row.isElectronic">增值税专用发票（纸质）</span>
        </template>
      </el-table-column>
      <el-table-column label="发票抬头">
        <template #default="scope">
          {{ scope.row.taxTitle }}
        </template>
      </el-table-column>
      <el-table-column label="纳税人识别号" align="center">
        <template #default="scope">
          {{ scope.row.taxNo }}
        </template>
      </el-table-column>
      <el-table-column label="发票金额" width="120" align="center">
        <template #default="scope">￥{{ scope.row.amount.toFixed(2) }}</template>
      </el-table-column>
      <el-table-column label="备注" align="center">
        <template #default="scope">
          {{ scope.row.memo }}
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="200">
        <template #default="scope">
          <el-link
            type="primary"
            style="margin-right: 5px"
            :underline="false"
            @click.prevent="btnOrderClick(scope.row)"
          >
            订 单
          </el-link>
          <el-link
            type="primary"
            style="margin-right: 5px"
            :underline="false"
            v-if="scope.row.status === statusDefault"
            @click.prevent="btnDeliverClick(scope.row)"
          >
            开 票
          </el-link>
          <el-link
            type="primary"
            style="margin-right: 5px"
            :underline="false"
            v-if="scope.row.status != statusCancellation"
            @click.prevent="btnCancellationClick(scope.row)"
          >
            作 废
          </el-link>
        </template>
      </el-table-column>
    </el-table>

    <el-drawer title="订单信息" direction="rtl" size="60%" v-if="orders" v-model="isTableOrders">
      <table-orders :orders="orders"></table-orders>
    </el-drawer>

    <el-drawer title="发票信息" direction="rtl" size="60%" v-if="invoice" v-model="isTableInvoice">
      <table-invoice :invoice="invoice" :status="getInvoiceStatus(invoice.status)"></table-invoice>
    </el-drawer>

    <el-dialog title="开具发票" v-model="isFormDelivery" width="60%">
      <form-delivery :invoice="invoice" @cancel="isFormDelivery = false" @submit="deliverySuccess"></form-delivery>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IInvoice, IOrder } from '@/types/models';
import utils from '@/utils';
import api from '@/api/admin/invoices';
import TableOrders from './components/invoices/TableOrders.vue';
import TableInvoice from './components/invoices/TableInvoice.vue';
import FormDelivery from './components/invoices/FormDelivery.vue';

const $statusDefault = 'Default';
const $statusDelivered = 'Delivered';
const $statusCancellation = 'Cancellation';

interface IState {
  statusDefault: string;
  statusDelivered: string;
  statusCancellation: string;
  status: string;
  items: IInvoice[];
  defaultCount: number;
  deliveredCount: number;
  cancellationCount: number;
  isTableOrders: boolean;
  isTableInvoice: boolean;
  isFormDelivery: boolean;
  invoice: IInvoice;
  orders: IOrder[];
}

export default defineComponent({
  components: {
    TableInvoice,
    TableOrders,
    FormDelivery,
  },
  data() {
    return {
      statusDefault: 'Default',
      statusDelivered: 'Delivered',
      statusCancellation: 'Cancellation',
      status: this.$route.query.status || 'Default',
      items: [],
      defaultCount: 0,
      deliveredCount: 0,
      cancellationCount: 0,
      isTableOrders: false,
      isTableInvoice: false,
      isFormDelivery: false,
      invoice: {} as IInvoice,
      orders: [],
    } as IState;
  },
  methods: {
    apiGet() {
      api.get(
        {
          status: this.status,
        },
        (res) => {
          this.items = res.items;
          this.defaultCount = res.defaultCount;
          this.deliveredCount = res.deliveredCount;
          this.cancellationCount = res.cancellationCount;
        },
      );
    },

    apiOrdersGet(invoice: IInvoice) {
      api.getOrders(
        {
          userName: invoice.userName,
          invoiceId: invoice.guid,
        },
        (res) => {
          this.orders = res.orders;
          this.invoice = invoice;
          this.isTableOrders = true;
        },
      );
    },

    apiCancellation(invoice: IInvoice) {
      api.cancellation(
        {
          invoiceId: invoice.id,
          status: this.status,
        },
        (res) => {
          if (!res) return;
          utils.success('发票作废成功！');
          this.apiGet();
        },
      );
    },

    deliverySuccess() {
      this.isFormDelivery = false;
      this.apiGet();
    },

    getInvoiceStatus(status: string) {
      if (status === $statusDefault) {
        return '待开发票';
      }
      if (status === $statusDelivered) {
        return '已寄出';
      }
      if (status === $statusCancellation) {
        return '作废发票';
      }
      return '待开发票';
    },

    btnNavClick() {
      this.apiGet();
    },

    btnOrderClick(invoice: IInvoice) {
      this.apiOrdersGet(invoice);
    },

    btnInvoiceClick(invoice: IInvoice) {
      this.isTableInvoice = true;
      this.invoice = invoice;
    },

    btnDeliverClick(invoice: IInvoice) {
      this.isFormDelivery = true;
      this.invoice = invoice;
    },

    btnCancellationClick(invoice: IInvoice) {
      utils.alertDelete(
        {
          title: '发票作废',
          text: `此操作将把发票${invoice.taxTitle}设置为作废，确定吗？`,
          button: '确认作废',
        },
        () => {
          this.apiCancellation(invoice);
        },
      );
    },
  },
  mounted() {
    this.apiGet();
  },
});
</script>
